import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';

import { ACTION_MENU_BUTTON_TYPE, componentsSize, INFO_BLOCK_TYPES, LABEL_TYPES } from 'constants/components';
import { PROJECT_NAME_REQUIRED_ERROR } from 'constants/commonProjects';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { ProjectActionButtons } from 'components/complex/ProjectActionButtons';
import { ActionsMenu, InfoBlock, Label, QuestionNameInput } from 'components/simple';

const NAME_RULES = {
  required: PROJECT_NAME_REQUIRED_ERROR,
  minLength: 2,
};

const ACTION_MENU_BUTTON_PROPS = {
  type: ACTION_MENU_BUTTON_TYPE.ICON,
  size: componentsSize.large,
  colored: true,
};

export const ProjectTitle = ({
  control = null,
  projectName = '',
  isDisabledEdit,
  actionsButtons,
  actionsMenuProps,
  withActionsMenu,
  nameInputProps = {},
  infoData = null,
  withProjectTypeLabel = false,
  isPublicProject = false,
}) => {
  const { error: nameInputError, onChange: onNameChange, ...restNameInputProps } = nameInputProps;
  const { isLgUp } = useBreakpointFlags();

  const projectLabelType = isPublicProject ? LABEL_TYPES.PUBLIC_PROJECT : LABEL_TYPES.PRIVATE_PROJECT;

  const getHandleNameChange = (onChange) => (value) => {
    onNameChange?.(value);
    onChange(value);
  };

  const renderProjectLabel = () => <Label labelType={projectLabelType} />;

  const renderNameSection = () => {
    if (isDisabledEdit) {
      return withProjectTypeLabel && isLgUp ? (
        renderProjectLabel()
      ) : (
        <Typography variant="h1">{projectName}</Typography>
      );
    }

    return (
      <Controller
        control={control}
        name="name"
        rules={NAME_RULES}
        render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
          <QuestionNameInput
            {...field}
            {...restNameInputProps}
            multiline
            innerRef={ref}
            inputProps={{ maxLength: 100 }}
            error={error || nameInputError}
            onChange={getHandleNameChange(onChange)}
          />
        )}
      />
    );
  };

  const renderHeaderRow = () => {
    return (
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={{ xs: 3, sm: 4.5, lg: 3 }}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        {withProjectTypeLabel && !isLgUp && renderProjectLabel()}

        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start" flex={1} width="100%">
          {renderNameSection()}

          {withActionsMenu && !isLgUp && <ActionsMenu {...actionsMenuProps} buttonProps={ACTION_MENU_BUTTON_PROPS} />}
        </Stack>

        <ProjectActionButtons
          actionsMenuProps={actionsMenuProps}
          withActionsMenu={withActionsMenu && isLgUp}
          actionsButtons={actionsButtons}
        />
      </Stack>
    );
  };

  return (
    <Stack spacing={{ xs: 4, lg: 5 }} mb={{ xs: 4, sm: 8, lg: 13 }}>
      {renderHeaderRow()}

      {withProjectTypeLabel && isLgUp && <Typography variant="h1">{projectName}</Typography>}

      {infoData?.type && <InfoBlock {...infoData} />}
    </Stack>
  );
};

ProjectTitle.propTypes = {
  control: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    watch: PropTypes.func,
    trigger: PropTypes.func,
    formState: PropTypes.shape({}),
  }),
  projectName: PropTypes.string,
  isDisabledEdit: PropTypes.bool.isRequired,
  isPublicProject: PropTypes.bool,
  withProjectTypeLabel: PropTypes.bool,
  actionsButtons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      show: PropTypes.bool.isRequired,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
      color: PropTypes.string,
      type: PropTypes.string,
    }),
  ).isRequired,
  withActionsMenu: PropTypes.bool.isRequired,
  actionsMenuProps: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.node,
        onClick: PropTypes.func,
        link: PropTypes.string,
        hideOption: PropTypes.bool,
      }),
    ).isRequired,
    id: PropTypes.string.isRequired,
  }).isRequired,
  nameInputProps: PropTypes.shape({
    placeholder: PropTypes.string,
    id: PropTypes.string,
    error: PropTypes.shape({ message: PropTypes.string }),
    onFocus: PropTypes.func,
    onChange: PropTypes.func,
  }),
  infoData: PropTypes.shape({
    text: PropTypes.string,
    type: PropTypes.oneOf(Object.values(INFO_BLOCK_TYPES)),
  }),
};
